import { useEffect, useRef, useState } from "react";
import axios from "axios";
import baseUrl from "../api/utils";

const stripImages = (list) =>
  list.map((b) => ({ ...b, imageFile: null, imageDataUrl: null }));

export default function useBillDraft({
  billType,     // "SALE" | "ESTIMATE" | "POS"
  bills,        // your bills (tabs) array
  activeTabId,
  enabled,      // true only for NEW bills (not edit/view/convert)
  hasContent,   // (bill) => true if the bill has anything typed
}) {
  const token = sessionStorage.getItem("token");
  const draftUrl = `${baseUrl}/draft/${billType}`;

  const [draftReady, setDraftReady] = useState(false);
  const timer = useRef(null);
  const savedTabIds = useRef(new Set());
  const billsRef = useRef([]);
  const activeRef = useRef(1);
  billsRef.current = bills;
  activeRef.current = activeTabId;

  const syncDraft = async (keepalive = false) => {
    const list = billsRef.current.filter(
      (b) => !savedTabIds.current.has(b.id) && hasContent(b)
    );
    const headers = { Authorization: token, "Content-Type": "application/json" };
    try {
      if (list.length > 0) {
        await fetch(draftUrl, {
          method: "PUT",
          headers,
          keepalive,
          body: JSON.stringify({
            bills: stripImages(list),
            activeTabId: activeRef.current,
          }),
        });
      } else {
        await fetch(draftUrl, { method: "DELETE", headers, keepalive });
      }
    } catch (e) {
      /* never break billing because of a draft problem */
    }
  };

  // call once when the page opens; returns { bills, activeTabId } or null
  const loadDraft = async () => {
    try {
      const res = await axios.get(draftUrl, { headers: { Authorization: token } });
      if (res.status === 200 && res.data) {
        const d = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        if (Array.isArray(d.bills) && d.bills.length > 0) return d;
      }
    } catch (e) {
      console.error("Draft load failed", e);
    }
    return null;
  };

  // call after a bill is saved successfully
  const markSaved = (tabId) => {
    savedTabIds.current.add(tabId);
    syncDraft();
  };

  // auto-save 800ms after the last change
  useEffect(() => {
    if (!enabled || !draftReady) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => syncDraft(), 800);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills, activeTabId, draftReady, enabled]);

  // save instantly when leaving the page
  useEffect(() => {
    if (!enabled || !draftReady) return;
    const flush = () => syncDraft(true);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftReady, enabled]);

  return { setDraftReady, loadDraft, markSaved };
}